/*
 * moon-stage.js: the moon customizer's preview stage. Everything around the
 * print that the star map customizer gained on 2026-09-25 (starmap-v2.js),
 * ported onto the moon DOM:
 *
 *   - phones: the site header bar collapses and its menu + cart buttons move
 *     onto the preview; the preview is sticky and collapses with the scroll
 *     (the print scales into the part still showing, no layout per frame);
 *     a minimise chevron; the notch band that fills the iOS status bar area.
 *   - Room view: the live print laid onto a photographed poster (matrix3d).
 *   - Full-screen viewer with zoom, pinch, pan and swipe-down to close.
 *   - Light / dark theme toggle (initial theme resolved in layout/theme.liquid).
 *   - The add-to-cart star trail, called from moon-myjs.js's submit_form.
 *
 * Roles on the moon DOM: .app__main.moon-stage is the panel (star: .sm2-main),
 * #print is the frame wrap that moves and scales (star: .sm2-frame-wrap), and
 * .custom-frame is the print surface inside it (star: .sm2-frame).
 *
 * UI only. moon-myjs.js is also loaded alone by the admin's cart thumbnail
 * host, which never loads this file; nothing here touches the design state.
 */
(function () {
  'use strict';
  if (window.__MOON_HEADLESS || window.MoonStage) return;

  // Same breakpoint as moon.css's stacked (phone / tablet) layout.
  var MOBILE_MQ = '(max-width: 991px)';

  // Shared between the collapse and Room view, as in starmap-v2.js.
  var roomCollapse = null;     // Room view's scene geometry for the collapse
  var onRoomLayout = null;     // Room view re-laid out: re-apply the collapse
  var roomRelayout = null;     // the collapse depth changed: re-lay Room view
  var roomCheck = null;        // re-map Room view if the print moved
  var getCollapseD = null;     // current collapse depth (px)

  function mobileNow() {
    return !!(window.matchMedia && window.matchMedia(MOBILE_MQ).matches);
  }
  function threeDActive(print) {
    return !!(print && (print.classList.contains('hidden-for-3d') || print.classList.contains('hidden-for-3d-framed')));
  }

  // ── Sticky, scroll-linked preview (phones, stacked layout) ─────────────
  function bindMobilePreview(main) {
    var toggle = document.getElementById('moon-preview-toggle');
    if (!toggle) return;
    var siteHeader = document.querySelector('.shopify-section-header');
    var frameWrap = document.getElementById('print');
    // The follow-along preview is deliberately compact: 76% leaves materially
    // more room for the controls below; scrolling back to the top restores it.
    var STUCK_FRAME_SCALE = 0.76;
    var stickyPaintQueued = false;
    var headerTrackUntil = 0;
    var expandedHeightTimer = null;
    var headerPositionCache = null;
    var animTimer = null;

    // ── Notch backdrop ──────────────────────────────────────────────────
    // Safari 26 draws only the page's normal scrolling content under the
    // status bar (fixed and sticky layers are clipped at its edge), so this
    // is in-flow content (see .moon-notch-band in the CSS) kept parked just
    // above the viewport, under the preview. Where CSS scroll-driven
    // animations exist it rides the scroll inside the engine, in step with
    // the sticky preview; otherwise script moves it, a frame behind.
    var notchBand = document.querySelector('body > .moon-notch-band');
    if (!notchBand) {
      notchBand = document.createElement('div');
      notchBand.className = 'moon-notch-band';
      notchBand.setAttribute('aria-hidden', 'true');
      document.body.appendChild(notchBand);
    }
    var NOTCH_REACH = 300;   // px above the viewport top (also in the CSS keyframes)
    var NOTCH_SCROLL_LINKED = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline', 'scroll()'));
    notchBand.classList.toggle('is-scroll-linked', NOTCH_SCROLL_LINKED);
    var lastBandY = null, lastBandH = null;

    // In Room view the band carries a copy of the room photo, laid exactly
    // over the real one, so under the notch the scene simply carries on.
    var bandImg = null, lastBandImgT = null, lastBandImgSize = null;
    function paintBandPhoto(show, mainRect, p) {
      var g = show ? roomCollapse : null;
      if (!g || !g.photo) {
        if (bandImg && bandImg.style.display !== 'none') bandImg.style.display = 'none';
        return;
      }
      if (!bandImg) {
        bandImg = document.createElement('img');
        bandImg.className = 'moon-notch-band__img';
        bandImg.alt = '';
        bandImg.decoding = 'async';
        notchBand.appendChild(bandImg);
      }
      var ph = g.photo, e = g.end;
      if (bandImg.getAttribute('src') !== ph.src) bandImg.setAttribute('src', ph.src);
      var size = Math.round(ph.w) + 'x' + Math.round(ph.h);
      if (size !== lastBandImgSize) {
        lastBandImgSize = size;
        bandImg.style.width = ph.w + 'px';
        bandImg.style.height = ph.h + 'px';
      }
      // Same map applyCollapse gives the real photo (P -> kP + p*t in the
      // panel's padding box), then into the band's coordinates.
      var k = 1 + p * (e.k - 1);
      var bandTop = NOTCH_SCROLL_LINKED ? -NOTCH_REACH
        : lastBandY - (window.pageYOffset || document.documentElement.scrollTop || 0);
      var x = mainRect.left + main.clientLeft + k * ph.left + p * e.tx;
      var y = mainRect.top + main.clientTop + k * ph.top + p * e.ty - bandTop;
      var t = 'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,0) scale(' + (+k.toFixed(5)) + ')';
      if (t !== lastBandImgT) { lastBandImgT = t; bandImg.style.transform = t; }
      if (bandImg.style.display === 'none') bandImg.style.display = '';
    }

    // iOS 26 Safari can leave the scroll-linked animation behind when the
    // page shortens under the reader (e.g. "Show fewer themes" clamps the
    // scroll): the band then sits hundreds of px too low and its room photo
    // shows over the sidebar. The band is already laid out here (mainRect was
    // just read), so checking it is cheap: restart the animation once, and if
    // it is still off, follow the scroll from JS for the rest of the visit.
    // Armed only once the band has been seen in place: on the first frames
    // the animation has not applied yet (transform none), which is not drift.
    var bandSeenInPlace = false;
    function checkBandTimeline() {
      if (!NOTCH_SCROLL_LINKED || !notchBand.getClientRects().length) return;   // hidden (landscape)
      if (Math.abs(notchBand.getBoundingClientRect().top + NOTCH_REACH) <= 3) { bandSeenInPlace = true; return; }
      if (!bandSeenInPlace) return;
      notchBand.style.animation = 'none';
      void notchBand.offsetWidth;
      notchBand.style.animation = '';
      if (Math.abs(notchBand.getBoundingClientRect().top + NOTCH_REACH) <= 3) return;
      NOTCH_SCROLL_LINKED = false;
      notchBand.classList.remove('is-scroll-linked');
      lastBandY = null;
      lastBandImgT = null;
    }

    function paintNotchBand(minimized, mainRect, p) {
      checkBandTimeline();
      // Reach below the viewport edge, hidden behind the preview; never past
      // its bottom (minimised it is ~49px; at the end of the page the preview
      // scrolls away and the band must not be left showing over the footer).
      var below = Math.min(minimized ? 40 : 200, Math.round(mainRect.bottom) - (minimized ? 9 : 80));
      var h = Math.max(0, NOTCH_REACH + below);
      if (h !== lastBandH) { lastBandH = h; notchBand.style.height = h + 'px'; }
      if (!NOTCH_SCROLL_LINKED) {
        var y = Math.round((window.pageYOffset || document.documentElement.scrollTop || 0) - NOTCH_REACH);
        if (y !== lastBandY) { lastBandY = y; notchBand.style.transform = 'translate3d(0,' + y + 'px,0)'; }
      }
      var room = main.classList.contains('moon-stage--room') && !minimized && !main.classList.contains('sm-gallery-viewing');
      if (notchBand.classList.contains('is-room') !== room) notchBand.classList.toggle('is-room', room);
      paintBandPhoto(room, mainRect, p);
    }

    // The theme's <sticky-header> keeps a scroll listener that toggles
    // -sticky/-hidden/animate on the header. On phones the header is pinned
    // static (inline style in moon-skylit.liquid), so that listener only adds
    // scroll-time work and class churn. Swapping the element for a plain div
    // fires its disconnectedCallback, which tears the listener down for good.
    // Desktop keeps the theme's sticky header as before.
    if (mobileNow()) {
      var stickyHeaderEl = document.querySelector('sticky-header');
      if (stickyHeaderEl) {
        var plain = document.createElement('div');
        plain.className = stickyHeaderEl.className;
        for (var i = 0; i < stickyHeaderEl.attributes.length; i++) {
          var attr = stickyHeaderEl.attributes[i];
          if (attr.name !== 'class') plain.setAttribute(attr.name, attr.value);
        }
        while (stickyHeaderEl.firstChild) plain.appendChild(stickyHeaderEl.firstChild);
        stickyHeaderEl.replaceWith(plain);
      }
      if (siteHeader) {
        siteHeader.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'animate');
        document.body.classList.remove('shopify-body-section-header-sticky', 'shopify-body-section-header-hidden');
      }
    }

    function paintToggle(minimized) {
      if (expandedHeightTimer) {
        window.clearTimeout(expandedHeightTimer);
        expandedHeightTimer = null;
      }
      main.classList.toggle('moon-stage--minimized', minimized);
      toggle.setAttribute('aria-expanded', minimized ? 'false' : 'true');
      toggle.setAttribute('aria-label', minimized ? 'Show preview' : 'Minimise preview');
      if (!minimized && main.style.getPropertyValue('--moon-preview-expanded-height')) {
        expandedHeightTimer = window.setTimeout(function () {
          main.style.removeProperty('--moon-preview-expanded-height');
          expandedHeightTimer = null;
        }, 340);
      }
    }

    var forceNextApply = false;
    function queueStickyPaint(trackHeader, force) {
      if (trackHeader) headerTrackUntil = Date.now() + 450;
      if (force) forceNextApply = true;
      if (stickyPaintQueued) return;
      stickyPaintQueued = true;
      window.requestAnimationFrame(paintStickyState);
    }

    // Last values written to the CSS custom properties: writing one
    // invalidates style for the subtree whether or not it changed, so only
    // write on change.
    var lastStickyTop = null;
    var lastCollapseD = null;
    var lastFrameBottom = null;

    // ── Scroll-linked collapse ───────────────────────────────────────────
    // The panel keeps ONE layout and sticks --moon-collapse px above the
    // viewport (see .moon-stage in the CSS), so for the first D px of scroll
    // it slides under the top edge with the page, and the print (or the
    // whole room scene) is scaled into the part still showing. Only
    // `translate`/`scale` change per frame: no layout, and the controls below
    // move exactly with the finger.
    // Stuck-state paddings the compact preview is built around: 6px above
    // #print, 6px + the 16px sheet lip below it.
    var STUCK_PAD_TOP = 6;
    var STUCK_PAD_BOTTOM = 22;
    var stackedMq = window.matchMedia ? window.matchMedia(MOBILE_MQ) : null;
    var shortLandscapeMq = window.matchMedia ? window.matchMedia('(orientation: landscape) and (max-height: 500px)') : null;
    var geom = null;
    var geomDirty = true;
    var lastP = -1;
    var lastRoomKey = null;
    var willChangeTimer = null;
    var collapseEls = null;

    function kids(sel) {
      return Array.prototype.filter.call(main.children, function (el) { return el.matches(sel); });
    }
    function collectEls() {
      collapseEls = {
        // Pinned to the panel's top edge: ride down with the visible top.
        top: kids('.moon-chrome-btn, #moon-gift-peek-btn, .moon-3d-toggle-btn'),
        // Centred on the panel: follow the centre of what is visible.
        middle: kids('.sm-gallery, .font-preview-name'),
        // Sit on the print's bottom edge (--moon-frame-bottom).
        bottom: kids('.cust-toolbar, .moon-room-fab, .moon-expand-fab, .cust-toolbar-fab, .sm-gallery-mobile-toggle, .moon-preview-toggle'),
        count: main.childElementCount
      };
    }

    function measureCollapse() {
      geomDirty = false;
      if (!frameWrap || !frameWrap.offsetHeight) return;
      var H = main.offsetHeight;
      var fw = frameWrap.offsetWidth, fh = frameWrap.offsetHeight;
      var ox = frameWrap.offsetLeft, oy = frameWrap.offsetTop;
      var k = STUCK_FRAME_SCALE;
      var D = Math.max(0, Math.round(H - STUCK_PAD_TOP - k * fh - STUCK_PAD_BOTTOM));
      // Flat end state as a similarity P -> kP + t in panel coordinates: the
      // print at 76%, centred, its top STUCK_PAD_TOP below the visible top (D).
      var Ox = ox + fw / 2, Oy = oy + fh / 2;   // #print scales about its centre
      geom = {
        H: H,
        D: D,
        flat: { k: k, tx: (1 - k) * Ox, ty: D + STUCK_PAD_TOP + k * fh / 2 - k * Oy },
        flatOrigin: [Ox, Oy],
        bottom0: Math.max(6, Math.round(H - (oy + fh))),
        bottom1: Math.max(6, Math.round(H - (D + STUCK_PAD_TOP + k * fh)))
      };
    }

    function setMotion(el, tx, ty, k) {
      var t = (Math.abs(tx) < 0.01 && Math.abs(ty) < 0.01) ? '' : (tx.toFixed(2) + 'px ' + ty.toFixed(2) + 'px');
      if (el.style.translate !== t) el.style.translate = t;
      if (k !== undefined) {
        var sc = Math.abs(k - 1) < 0.0001 ? '' : String(+k.toFixed(5));
        if (el.style.scale !== sc) el.style.scale = sc;
      }
    }

    function applyCollapse(p, force) {
      var roomGeom = main.classList.contains('moon-stage--room') ? roomCollapse : null;
      var roomKey = roomGeom ? roomGeom.key : 'flat';
      if (!force && Math.abs(p - lastP) < 0.0005 && roomKey === lastRoomKey) return;
      lastP = p;
      lastRoomKey = roomKey;
      // moon-ui.js (3D toggle) and starmap-gallery.js add children after this
      // binds: re-collect when the panel's children change.
      if (!collapseEls || collapseEls.count !== main.childElementCount) collectEls();
      var D = geom ? geom.D : 0;

      // The picture: the print alone, or in Room view the scene, its light map
      // and the print lying in it, moved as one rigid piece. Each element has
      // its own transform-origin O, so the shared map P -> kP + t becomes
      // translate = t - (1 - k)O per element.
      var end = roomGeom ? roomGeom.end : (geom ? geom.flat : null);
      var items = roomGeom ? roomGeom.items : (geom && frameWrap ? [[frameWrap, geom.flatOrigin[0], geom.flatOrigin[1]]] : []);
      if (!roomGeom && roomCollapse) {
        // Room view just switched off: leave nothing on the scene layers.
        roomCollapse.items.forEach(function (it) { if (it[0] !== frameWrap) setMotion(it[0], 0, 0, 1); });
      }
      if (end) {
        var k = 1 + p * (end.k - 1);
        items.forEach(function (it) {
          setMotion(it[0], p * end.tx - (1 - k) * it[1], p * end.ty - (1 - k) * it[2], k);
        });
      }

      collapseEls.top.forEach(function (el) { setMotion(el, 0, p * D); });
      collapseEls.middle.forEach(function (el) { setMotion(el, 0, p * D / 2); });
      var drop = (!roomGeom && geom) ? p * (geom.bottom0 - geom.bottom1) : 0;
      collapseEls.bottom.forEach(function (el) { setMotion(el, 0, drop); });

      // A composited layer for the picture while it moves, dropped 180ms after
      // it stops so the browser re-rasters it sharp at the scale it settled on.
      items.forEach(function (it) { if (it[0].style.willChange !== 'transform') it[0].style.willChange = 'transform'; });
      if (willChangeTimer) window.clearTimeout(willChangeTimer);
      willChangeTimer = window.setTimeout(function () {
        willChangeTimer = null;
        [frameWrap].concat(roomCollapse ? roomCollapse.items.map(function (it) { return it[0]; }) : [])
          .forEach(function (el) { if (el) el.style.willChange = ''; });
      }, 180);
    }
    // Room view re-lays its scene out (resize, finish, size change); re-apply.
    onRoomLayout = function () { queueStickyPaint(false, true); };
    getCollapseD = function () { return geom ? geom.D : 0; };

    // The print leaves the stage in the gallery viewer and in 3D: no collapse
    // then, the whole panel pins at the top edge (like minimised).
    function printAway() {
      return main.classList.contains('sm-gallery-viewing') || threeDActive(frameWrap);
    }

    function paintStickyState() {
      stickyPaintQueued = false;
      var mobile = mobileNow();
      var stacked = !!(stackedMq && stackedMq.matches && !(shortLandscapeMq && shortLandscapeMq.matches));
      var headerBottom = 0;
      if (mobile && siteHeader) {
        if (headerPositionCache === null) headerPositionCache = window.getComputedStyle(siteHeader).position;
        if (headerPositionCache !== 'static') {
          headerBottom = Math.max(0, Math.ceil(siteHeader.getBoundingClientRect().bottom));
        }
      }
      if (headerBottom !== lastStickyTop) {
        lastStickyTop = headerBottom;
        document.documentElement.style.setProperty('--moon-sticky-top', headerBottom + 'px');
      }
      var stickyTop = headerBottom;
      var minimized = main.classList.contains('moon-stage--minimized');
      var away = printAway();
      main.classList.toggle('moon-stage--print-away', away);
      var animating = main.classList.contains('moon-stage--anim');

      // Measure the one layout, only while it is the real, full panel.
      var geomChanged = false;
      if (geomDirty && stacked && !minimized && !away && !animating) {
        var before = geom ? geom.D + ',' + geom.bottom0 + ',' + geom.H : '';
        measureCollapse();
        geomChanged = (geom ? geom.D + ',' + geom.bottom0 + ',' + geom.H : '') !== before;
      }
      var D = (stacked && geom) ? geom.D : 0;
      if (D !== lastCollapseD) {
        lastCollapseD = D;
        main.style.setProperty('--moon-collapse', D + 'px');
        if (roomRelayout) roomRelayout();
      }
      var bottom0 = (stacked && geom) ? geom.bottom0 : null;
      var frameBottom = main.classList.contains('moon-stage--room') ? 24 : bottom0;
      if (frameBottom !== null && frameBottom !== lastFrameBottom) {
        lastFrameBottom = frameBottom;
        main.style.setProperty('--moon-frame-bottom', frameBottom + 'px');
      }

      var mainRect = main.getBoundingClientRect();
      if (roomCheck) roomCheck();
      var p = 0;
      if (stacked && !minimized && !away && D > 0) {
        p = Math.min(1, Math.max(0, (stickyTop - mainRect.top) / D));
      }
      var wasStuck = main.classList.contains('moon-stage--stuck');
      var stuck;
      if (minimized || away) {
        // Pinned at the top edge whole; "stuck" once the page has scrolled
        // it there. Hysteresis stops a flicker on the exact line.
        var scrolled = (window.pageYOffset || document.documentElement.scrollTop || 0) > (wasStuck ? 0 : 8);
        stuck = stacked && scrolled && mainRect.top <= stickyTop + (wasStuck ? 8 : 1);
      } else {
        stuck = stacked && p >= (wasStuck ? 0.9 : 0.98);
      }
      if (stuck !== wasStuck) main.classList.toggle('moon-stage--stuck', stuck);
      if (stacked) paintNotchBand(minimized, mainRect, p);

      var force = forceNextApply || geomChanged;
      forceNextApply = false;
      applyCollapse(stacked ? p : 0, force);

      if (!stuck && minimized) paintToggle(false);
      if ((headerPositionCache && headerPositionCache !== 'static' && Date.now() < headerTrackUntil) || animating) {
        queueStickyPaint(false);
      }
    }

    // Run the customer-initiated toggles with transitions on, then take them
    // off again so no scroll-driven change can inherit them.
    // Scroll anchoring is off for the length of the toggle: Chromium would
    // otherwise hold the controls still by scrolling the page up as the panel
    // shrinks, landing at the top, which reads as "back at the top" and
    // expands the preview again at once. (WebKit has no scroll anchoring.)
    var rootStyle = document.documentElement.style;
    function withAnim(fn) {
      if (animTimer) window.clearTimeout(animTimer);
      main.classList.add('moon-stage--anim');
      rootStyle.overflowAnchor = 'none';
      fn();
      queueStickyPaint(false);
      animTimer = window.setTimeout(function () {
        animTimer = null;
        rootStyle.overflowAnchor = '';
        main.classList.remove('moon-stage--anim');
        geomDirty = true;
        queueStickyPaint(false, true);
      }, 320);
    }

    paintToggle(false);

    toggle.addEventListener('click', function () {
      var minimize = !main.classList.contains('moon-stage--minimized');
      withAnim(function () {
        if (minimize) {
          // Lock the expanded pixel height before toggling the class so WebKit
          // can interpolate from it rather than snapping between auto heights.
          main.style.setProperty('--moon-preview-expanded-height', main.getBoundingClientRect().height + 'px');
          main.getBoundingClientRect();
        }
        paintToggle(minimize);
      });
      queueStickyPaint(false);
    });
    window.addEventListener('scroll', function () { queueStickyPaint(true); }, { passive: true });
    window.addEventListener('resize', function () {
      headerPositionCache = null;
      // Keep lastStickyTop / lastFrameBottom: the next paint re-measures and
      // compares. Clearing them rewrote --moon-sticky-top on <html> with the
      // same value on every resize, and iOS fires one per toolbar show/hide
      // mid-scroll; a root custom-property write restyles the whole document
      // (~350ms here), which held the scroll-linked notch band back.
      geomDirty = true;
      queueStickyPaint(true, true);
    });
    // A new size / finish resizes the print; minimise resizes the panel.
    // Transforms don't fire this, so the collapse itself never does.
    if (window.ResizeObserver) {
      var collapseRo = new ResizeObserver(function () {
        geomDirty = true;
        queueStickyPaint(false, true);
      });
      collapseRo.observe(main);
      if (frameWrap) collapseRo.observe(frameWrap);
    }

    // starmap-gallery.js toggles .sm-gallery-viewing on the panel, Room view
    // .moon-stage--room, and the 3D view hides #print. Re-measure the moment
    // the frame comes back.
    if (window.MutationObserver) {
      var sig = function () {
        return [main.classList.contains('sm-gallery-viewing'), main.classList.contains('moon-stage--room'), threeDActive(frameWrap)].join();
      };
      var lastSig = sig();
      var onMut = function () {
        var s = sig();
        if (s === lastSig) return;
        lastSig = s;
        geomDirty = true;
        queueStickyPaint(false, true);
      };
      new MutationObserver(onMut).observe(main, { attributes: true, attributeFilter: ['class'] });
      if (frameWrap) new MutationObserver(onMut).observe(frameWrap, { attributes: true, attributeFilter: ['class'] });
    }

    queueStickyPaint(true);
  }

  // The tools flyout (moon-ui.js) is anchored to the print's bottom edge,
  // which the gallery viewer and the minimised bar both take away.
  function bindToolsExtras(main) {
    var fab = document.getElementById('moonToolbarFab');
    if (!fab || !window.MutationObserver) return;
    new MutationObserver(function () {
      if (!main.classList.contains('app__main--tools-open')) return;
      if (main.classList.contains('sm-gallery-viewing') || main.classList.contains('moon-stage--minimized')) fab.click();
    }).observe(main, { attributes: true, attributeFilter: ['class'] });
    var theme = document.getElementById('moonToolbarTheme');
    if (theme) theme.addEventListener('click', function () {
      // Theme is a one-shot switch like Info and Save: close the flyout.
      if (main.classList.contains('app__main--tools-open')) fab.click();
    });
  }

  // ── Room view ───────────────────────────────────────────────────────────
  // Lays the live print onto the blank poster in a photographed scene
  // (#moon-room-scenes in the section: image, poster corners, months). The
  // print is the real #print, transformed with a matrix3d, so every edit
  // shows up in the room immediately and the text stays exact.
  //
  // Geometry: the poster is a 5:7 sheet. The print keeps its own aspect and
  // is sized to COVER the sheet, so no blank paper ever peeks out and
  // nothing is stretched.
  var SHEET_ASPECT = 5 / 7;

  function homography(src, dst) {
    // Solve the 8 unknowns of H (h33 = 1) mapping 4 src points onto 4 dst.
    var A = [], b = [];
    for (var i = 0; i < 4; i++) {
      var x = src[i][0], y = src[i][1], u = dst[i][0], v = dst[i][1];
      A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
      A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
    }
    for (var c = 0; c < 8; c++) {
      var piv = c;
      for (var r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[piv][c])) piv = r;
      var tA = A[c]; A[c] = A[piv]; A[piv] = tA;
      var tb = b[c]; b[c] = b[piv]; b[piv] = tb;
      for (var r2 = c + 1; r2 < 8; r2++) {
        var f = A[r2][c] / A[c][c];
        for (var k = c; k < 8; k++) A[r2][k] -= f * A[c][k];
        b[r2] -= f * b[c];
      }
    }
    var h = new Array(8);
    for (var r3 = 7; r3 >= 0; r3--) {
      var sum = b[r3];
      for (var k2 = r3 + 1; k2 < 8; k2++) sum -= A[r3][k2] * h[k2];
      h[r3] = sum / A[r3][r3];
    }
    return [[h[0], h[1], h[2]], [h[3], h[4], h[5]], [h[6], h[7], 1]];
  }
  function applyH(H, x, y) {
    var w = H[2][0] * x + H[2][1] * y + H[2][2];
    return [(H[0][0] * x + H[0][1] * y + H[0][2]) / w, (H[1][0] * x + H[1][1] * y + H[1][2]) / w];
  }
  function matrix3d(H) {
    return 'matrix3d(' + [
      H[0][0], H[1][0], 0, H[2][0],
      H[0][1], H[1][1], 0, H[2][1],
      0, 0, 1, 0,
      H[0][2], H[1][2], 0, H[2][2]
    ].map(function (n) { return +n.toFixed(8); }).join(',') + ')';
  }

  function bindRoomView(main) {
    var wrap = document.getElementById('print');
    var bg = document.getElementById('moon-room-bg');
    var light = document.getElementById('moon-room-light');
    var dataEl = document.getElementById('moon-room-scenes');
    var toggles = document.querySelectorAll('[data-moon-room-toggle]');
    if (!wrap || !bg || !light || !dataEl || !toggles.length) return;
    var scenes;
    try { scenes = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!scenes || !scenes.length) return;

    // ?room=<id> previews a scene; otherwise the one in season.
    var scene = null;
    try {
      var want = new URLSearchParams(window.location.search).get('room');
      if (want) scene = scenes.filter(function (s) { return s.id === want; })[0] || null;
    } catch (e) { /* noop */ }
    if (!scene) {
      var month = new Date().getMonth() + 1;
      scene = scenes.filter(function (s) { return (s.months || []).indexOf(month) !== -1; })[0] || scenes[0];
    }

    var on = false, raf = 0;
    var roomEl = document.getElementById('moon-room') || bg.parentNode;
    var backdrop = null;

    // Which photo of the scene is showing: the portrait original, or its wide
    // landscape outpaint (scene.wide) on desktop-shaped panels, whichever
    // aspect is closer to the panel's. A change waits until the new photo is
    // decoded, then swaps and re-lays out in the same frame.
    var view = scene, viewSwitching = false;
    function pickView(W, H) {
      if (!scene.wide || !W || !H) return scene;
      var a = W / H;
      return Math.abs(Math.log(a * scene.wide.h / scene.wide.w)) < Math.abs(Math.log(a * scene.h / scene.w)) ? scene.wide : scene;
    }
    function useView(v) {
      view = v;
      bg.src = v.src;
      light.src = v.light || v.src;
    }

    // Cover the panel, centred on the poster, never showing past the photo's
    // edges; but never let the poster grow past POSTER_MAX of the panel
    // height (on a wide desktop panel "cover" alone blew the print up past
    // the panel). When the cap wins, the photo stops short of the panel on
    // that axis and stays centred on the poster.
    var POSTER_MAX = 0.8;
    function roomFit(W, H) {
      var q = view.quad;
      var qTop = Math.min(q[0][1], q[1][1], q[2][1], q[3][1]);
      var qBot = Math.max(q[0][1], q[1][1], q[2][1], q[3][1]);
      var qcx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4;
      var qcy = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
      var sCover = Math.max(W / view.w, H / view.h);
      var sCap = POSTER_MAX * H / Math.max(1, qBot - qTop);
      var sc = Math.min(sCover, sCap);
      var iw = view.w * sc, ih = view.h * sc;
      var left = iw >= W ? Math.min(0, Math.max(W - iw, W / 2 - qcx * sc)) : W / 2 - qcx * sc;
      var top = ih >= H ? Math.min(0, Math.max(H - ih, H / 2 - qcy * sc)) : H / 2 - qcy * sc;
      return { s: sc, left: left, top: top };
    }

    function schedule() { if (on && !raf) raf = requestAnimationFrame(layout); }

    function layout() {
      raf = 0;
      if (!on) return;
      var W = main.clientWidth, Hc = main.clientHeight;
      var fw = wrap.offsetWidth, fh = wrap.offsetHeight;
      if (!W || !Hc || !fw || !fh) return;

      var wantView = pickView(W, Hc);
      if (wantView !== view && !viewSwitching) {
        viewSwitching = true;
        var pre = new Image();
        pre.src = wantView.src;
        (pre.decode ? pre.decode() : Promise.resolve()).catch(function () {}).then(function () {
          viewSwitching = false;
          if (!on || pickView(main.clientWidth, main.clientHeight) !== wantView) { schedule(); return; }
          useView(wantView);
          layout();
        });
      }
      var q = view.quad;
      var fit = roomFit(W, Hc);
      var s = fit.s, left = fit.left, top = fit.top;
      var iw = view.w * s, ih = view.h * s;
      [bg, light].forEach(function (img) {
        img.style.width = iw + 'px'; img.style.height = ih + 'px';
        img.style.left = left + 'px'; img.style.top = top + 'px';
      });
      // Where the photo stops short of the panel (wide desktop panels), a
      // blurred copy of the scene fills the rest and the photo's edges fade
      // into it, so it reads as one room rather than a picture in a box.
      var gapX = iw < W - 0.5, gapY = ih < Hc - 0.5;
      roomEl.classList.toggle('is-contained', gapX || gapY);
      var fade = [];
      if (gapX) fade.push('linear-gradient(to right, transparent 0, #000 9%, #000 91%, transparent 100%)');
      if (gapY) fade.push('linear-gradient(to bottom, transparent 0, #000 9%, #000 91%, transparent 100%)');
      var mask = fade.length ? fade.join(', ') : '';
      bg.style.webkitMaskImage = bg.style.maskImage = mask;
      bg.style.webkitMaskComposite = fade.length > 1 ? 'source-in' : '';
      bg.style.maskComposite = fade.length > 1 ? 'intersect' : '';
      if (gapX || gapY) {
        if (!backdrop) {
          backdrop = document.createElement('div');
          backdrop.className = 'moon-room__backdrop';
          roomEl.insertBefore(backdrop, roomEl.firstChild);
        }
        var src = 'url("' + (bg.currentSrc || bg.src) + '")';
        if (backdrop.style.backgroundImage !== src) backdrop.style.backgroundImage = src;
      }

      // The PRINT goes on the sheet (.custom-frame; a Framed finish's wood
      // ring is its ::after, 22px outside, so it extends past the sheet as a
      // real frame around a print that size would).
      var frame = wrap.querySelector('.custom-frame');
      var px0 = 0, py0 = 0, pWidth = fw, pHeight = fh;
      if (frame && frame.clientWidth && frame.clientHeight) {
        px0 = frame.offsetLeft + frame.clientLeft;
        py0 = frame.offsetTop + frame.clientTop;
        pWidth = frame.clientWidth;
        pHeight = frame.clientHeight;
      }

      // Print rectangle in sheet units (sheet = SHEET_ASPECT x 1), covering it.
      var ap = pWidth / pHeight;
      var ph = Math.max(1, SHEET_ASPECT / ap) * 1.012;
      var pw = ph * ap;
      var u0 = (SHEET_ASPECT - pw) / 2 / SHEET_ASPECT, u1 = (SHEET_ASPECT + pw) / 2 / SHEET_ASPECT;
      var v0 = (1 - ph) / 2, v1 = (1 + ph) / 2;
      var sheet = homography([[0, 0], [1, 0], [1, 1], [0, 1]], q);
      var corners = [[u0, v0], [u1, v0], [u1, v1], [u0, v1]].map(function (c) {
        var p = applyH(sheet, c[0], c[1]);
        return [p[0] * s + left, p[1] * s + top];   // panel coordinates
      });

      // #print keeps its layout box; map that box onto the corners.
      var ox = wrap.offsetLeft, oy = wrap.offsetTop;
      placedAt = placementKey();
      var M = homography(
        [[px0, py0], [px0 + pWidth, py0], [px0 + pWidth, py0 + pHeight], [px0, py0 + pHeight]],
        corners.map(function (c) { return [c[0] - ox, c[1] - oy]; }));
      wrap.style.setProperty('transform-origin', '0 0', 'important');
      wrap.style.setProperty('transform', matrix3d(M), 'important');

      // Lamplight across the whole object: the print, a Framed moulding and
      // any gift wrap / ribbon. The light map is a smooth field fitted to the
      // blank sheet, so extending it past the sheet adds no floor texture.
      var ring = frame && /\bframe-(black|white|natural|dark-brown)\b/.test(frame.className) ? 22 : 2;
      var outer = [[px0 - ring, py0 - ring], [px0 + pWidth + ring, py0 - ring],
        [px0 + pWidth + ring, py0 + pHeight + ring], [px0 - ring, py0 + pHeight + ring]].map(function (c) {
        var qq = applyH(M, c[0], c[1]);
        return [qq[0] + ox, qq[1] + oy];
      });
      // Whole pixels: a fractional polygon makes Chromium draw a hairline
      // seam along the multiplied layer's bounding box.
      light.style.clipPath = 'polygon(' + outer.map(function (c) {
        return Math.round(c[0] - left) + 'px ' + Math.round(c[1] - top) + 'px';
      }).join(', ') + ')';

      // Where the scene ends up once the phone preview has collapsed: the
      // same cover fit, into the D px shorter strip still showing. As one
      // similarity P -> kP + t on the panel it moves photo, light map and
      // print together; linear steps between two covering fits always cover.
      var D = getCollapseD ? getCollapseD() : 0;
      var Hc1 = Math.max(1, Hc - D);
      var fit1 = roomFit(W, Hc1);
      var s1 = fit1.s, left1 = fit1.left, top1 = fit1.top;
      var kR = s1 / s;
      bg.style.transformOrigin = light.style.transformOrigin = '0 0';
      sampleEdgeColor(s1, left1, top1, W);
      roomCollapse = {
        key: [W, Hc, D, left, top, s, ox, oy].map(function (n) { return Math.round(n * 100); }).join(','),
        end: { k: kR, tx: left1 - kR * left, ty: D + top1 - kR * top },
        items: [[bg, left, top], [light, left, top], [wrap, ox, oy]],
        photo: { src: bg.currentSrc || bg.src, w: iw, h: ih, left: left, top: top }
      };
      if (onRoomLayout) onRoomLayout();
    }
    roomRelayout = schedule;

    // The print is pinned to the photo by its layout position in the panel.
    // Resize, size changes and panel classes re-map it, but the print can also
    // move without any of those firing (iOS applying the layout late after a
    // rotation, the panel's contents shifting): it then sits off its sheet in
    // the photo. Compare on every sticky paint and after resizes settle.
    var placedAt = null;
    function placementKey() {
      var fr = wrap.querySelector('.custom-frame');
      return [main.clientWidth, main.clientHeight, wrap.offsetLeft, wrap.offsetTop, wrap.offsetWidth, wrap.offsetHeight,
        fr ? fr.offsetLeft + fr.clientLeft : 0, fr ? fr.offsetTop + fr.clientTop : 0].join(',');
    }
    roomCheck = function () {
      if (!on || raf) return;
      if (placementKey() !== placedAt) schedule();
    };
    var settleTimers = [];
    function settleChecks() {
      settleTimers.forEach(clearTimeout);
      settleTimers = [250, 700, 1500].map(function (ms) { return setTimeout(function () { if (roomCheck) roomCheck(); }, ms); });
    }
    window.addEventListener('resize', settleChecks);
    window.addEventListener('orientationchange', settleChecks);
    window.addEventListener('pageshow', settleChecks);

    // What sits above the viewport edge once the preview has collapsed, as a
    // left-to-right gradient of the photo's colours there: the notch band
    // wears it in Room view. Theme assets are same-origin, so the canvas read
    // is allowed; if not, the CSS fallback stays.
    var EDGE_COLUMNS = 12, EDGE_SPAN = 16;
    var edgeKey = null, edgeCanvas = null;
    function sampleEdgeColor(s1, left1, top1, W) {
      var key = [view.src, Math.round(s1 * 1e4), Math.round(left1), Math.round(top1), W].join(',');
      if (key === edgeKey || !bg.naturalWidth) return;
      edgeKey = key;
      try {
        var sx = Math.max(0, -left1 / s1);
        var sy1 = Math.max(0, -top1 / s1);
        var sy0 = Math.max(0, sy1 - EDGE_SPAN / s1);
        if (sy1 - sy0 < 1) sy1 = Math.min(view.h, sy0 + EDGE_SPAN / s1);
        var sw = Math.min(view.w - sx, W / s1);
        if (sw <= 0) return;
        var r = bg.naturalWidth / view.w;
        var CW = 8, CH = 4, cw = EDGE_COLUMNS * CW;
        edgeCanvas = edgeCanvas || document.createElement('canvas');
        edgeCanvas.width = cw; edgeCanvas.height = CH;
        var ctx = edgeCanvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bg, sx * r, sy0 * r, sw * r, (sy1 - sy0) * r, 0, 0, cw, CH);
        var d = ctx.getImageData(0, 0, cw, CH).data, stops = [], avg = [0, 0, 0];
        for (var c = 0; c < EDGE_COLUMNS; c++) {
          var sum = [0, 0, 0];
          for (var yy = 0; yy < CH; yy++) {
            for (var xx = c * CW; xx < (c + 1) * CW; xx++) {
              var o = (yy * cw + xx) * 4;
              sum[0] += d[o]; sum[1] += d[o + 1]; sum[2] += d[o + 2];
            }
          }
          var col = sum.map(function (v) { return Math.round(v / (CW * CH)); });
          stops.push('rgb(' + col.join(',') + ') ' + ((c + 0.5) / EDGE_COLUMNS * 100).toFixed(1) + '%');
          avg[0] += col[0]; avg[1] += col[1]; avg[2] += col[2];
        }
        var band = document.querySelector('body > .moon-notch-band');
        if (!band) return;
        band.style.setProperty('--moon-edge-color', 'rgb(' + avg.map(function (v) { return Math.round(v / EDGE_COLUMNS); }).join(',') + ')');
        band.style.setProperty('--moon-edge-fill', 'linear-gradient(to right, ' + stops.join(', ') + ')');
      } catch (e) { /* tainted or unavailable: keep the CSS fallback */ }
    }

    function paint() {
      Array.prototype.forEach.call(toggles, function (t) {
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
        t.setAttribute('aria-label', on ? 'Switch to the flat preview' : 'See it in a room');
        t.title = on ? 'Flat preview' : 'See it in a room';
      });
    }

    // First time room view opens on its own, point at the button that turns
    // it off. Once per browser (shared with the star map), fades after a few
    // seconds or the first tap.
    var HINT_KEY = 'skylit-room-hint-seen';
    function showHint() {
      try { if (localStorage.getItem(HINT_KEY)) return; localStorage.setItem(HINT_KEY, '1'); } catch (e) { return; }
      var btn = Array.prototype.filter.call(toggles, function (t) { return t.offsetParent !== null; })[0];
      if (!btn) return;
      var hint = document.createElement('div');
      hint.className = 'moon-room-hint';
      hint.setAttribute('role', 'status');
      hint.textContent = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches ? 'Tap' : 'Click') + ' for flat view';
      main.appendChild(hint);
      var m = main.getBoundingClientRect(), b = btn.getBoundingClientRect();
      hint.style.top = (b.top - m.top + b.height / 2) + 'px';
      hint.style.right = (m.right - b.left + 10) + 'px';
      requestAnimationFrame(function () { hint.classList.add('is-shown'); });
      var done = false;
      var dismiss = function () {
        if (done) return;
        done = true;
        hint.classList.remove('is-shown');
        document.removeEventListener('pointerdown', dismiss, true);
        window.removeEventListener('scroll', dismiss);
        setTimeout(function () { if (hint.parentNode) hint.parentNode.removeChild(hint); }, 300);
      };
      setTimeout(dismiss, 5000);
      setTimeout(function () {
        document.addEventListener('pointerdown', dismiss, true);
        window.addEventListener('scroll', dismiss, { passive: true });
      }, 400);
    }

    // ── On / off, with a crossfade ───────────────────────────────────────
    // Room view is the default: the page first paints the flat print (the
    // LCP poster), the scene loads after the page has, and then the flat
    // print fades out and back in lying in the room. A customer who switches
    // to flat is remembered (per browser, shared with the star map).
    var PREF_KEY = 'skylit-room-view';
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var sceneReady = null, fadeTimer = 0, userChose = false;

    function loadScene() {
      if (sceneReady) return sceneReady;
      view = pickView(main.clientWidth, main.clientHeight);
      sceneReady = Promise.all([[bg, view.src], [light, view.light || view.src]].map(function (pair) {
        return new Promise(function (resolve) {
          var img = pair[0];
          img.onload = img.onerror = function () { resolve(); };
          img.src = pair[1];
          if (img.complete && img.naturalWidth) resolve();
        });
      }));
      return sceneReady;
    }

    function apply(next) {
      on = next;
      if (on) {
        main.classList.add('moon-stage--room');
        layout();
      } else {
        main.classList.remove('moon-stage--room');
        wrap.style.removeProperty('transform');
        wrap.style.removeProperty('transform-origin');
        light.style.clipPath = '';
        if (onRoomLayout) onRoomLayout();
      }
      paint();
      // The gift overlays measure the frame on screen (moon-ui.js); re-place
      // them against the new geometry.
      if (window.repositionMoonOverlays) window.repositionMoonOverlays();
    }

    function crossfade(fn) {
      clearTimeout(fadeTimer);
      if (reduce) { fn(); return; }
      main.classList.add('moon-stage--fading');
      fadeTimer = setTimeout(function () {
        fn();
        main.classList.add('moon-stage--room-enter');
        void main.offsetWidth;
        main.classList.remove('moon-stage--fading');
        fadeTimer = setTimeout(function () { main.classList.remove('moon-stage--room-enter'); }, 480);
      }, 190);
    }

    function set(next) {
      if (next) {
        loadScene().then(function () {
          if (!on) crossfade(function () { apply(true); });
        });
      } else if (on) {
        crossfade(function () { apply(false); });
      }
    }

    Array.prototype.forEach.call(toggles, function (t) {
      t.addEventListener('click', function () {
        userChose = true;
        var next = !on;
        try { localStorage.setItem(PREF_KEY, next ? 'room' : 'flat'); } catch (e) { /* not remembered */ }
        set(next);
      });
    });

    var pref = null;
    try { pref = localStorage.getItem(PREF_KEY); } catch (e) { /* storage blocked */ }
    if (pref !== 'flat') {
      var startDefault = function () {
        var go = function () {
          if (userChose) return;
          set(true);
          loadScene().then(function () { setTimeout(function () { if (on && !userChose) showHint(); }, 900); });
        };
        if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 1500 });
        else setTimeout(go, 300);
      };
      if (document.readyState === 'complete') startDefault();
      else window.addEventListener('load', startDefault, { once: true });
    }

    window.addEventListener('resize', schedule);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(schedule);
      ro.observe(main); ro.observe(wrap);
      var frameEl = wrap.querySelector('.custom-frame');
      if (frameEl) ro.observe(frameEl);
    }
    // Stuck/unstuck and minimise change the panel; a finish change swaps the
    // frame's classes (the wood ring widens the light map).
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(main, { attributes: true, attributeFilter: ['class'] });
      var fr = wrap.querySelector('.custom-frame');
      if (fr) new MutationObserver(schedule).observe(fr, { attributes: true, attributeFilter: ['class'] });
    }
    paint();
  }

  // ── Full-screen print viewer ────────────────────────────────────────────
  // Any [data-moon-viewer-open] button opens #moon-viewer with a snapshot of
  // the live print. The clone keeps the on-page pixel size and is scaled with
  // a transform (fit x zoom) instead of being re-laid out at viewer size: the
  // caption runs on cqh/cqw and the wood ring is a fixed 22px, so scaling is
  // the only way every element keeps its exact proportion to the print.
  // "100%" = the whole print fitted to the stage.
  function bindPrintViewer() {
    var viewer = document.getElementById('moon-viewer');
    var stage = document.getElementById('moon-viewer-stage');
    var canvas = document.getElementById('moon-viewer-canvas');
    var level = document.getElementById('moon-viewer-level');
    var fsBtn = document.getElementById('moon-viewer-fs');
    var hint = document.getElementById('moon-viewer-hint');
    var openers = document.querySelectorAll('[data-moon-viewer-open]');
    if (!viewer || !stage || !canvas || !openers.length) return;

    // Park the dialog on <body>: inside the section it would inherit the
    // stacking context of the sticky preview on mobile.
    if (viewer.parentNode !== document.body) document.body.appendChild(viewer);

    var MIN = 1, MAX = 5;
    var STEPS = [1, 1.5, 2, 3, 4, 5];
    var baseW = 0, baseH = 0, fit = 1;
    var zoom = 1, tx = 0, ty = 0;
    var returnFocus = null;
    var prevOverflow = '';
    var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

    if (hint) hint.textContent = coarse ? 'Pinch to zoom, swipe down to close.' : 'Zoom in, then drag to explore.';

    function fsElement() { return document.fullscreenElement || document.webkitFullscreenElement || null; }
    var fsSupported = !!(viewer.requestFullscreen || viewer.webkitRequestFullscreen) &&
      !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
    if (fsBtn) fsBtn.hidden = !fsSupported;

    // The frame moulding is a 9-slice border-image. Scaled up here, the
    // browser paints its corner and edge tiles separately and their
    // antialiased edges show as hairlines at the mitres. Paint the same nine
    // slices into one canvas laid over the box instead: one bitmap scales
    // without seams. `cs` is the computed style carrying the border-image,
    // `bw` the border widths [top, right, bottom, left] in CSS px.
    // `onPainted` runs once the canvas holds the frame, so the caller can
    // drop the original border-image without a blank frame in between.
    function paintFrameRing(cs, w, h, bw, onPainted) {
      var src = /url\(\s*["']?([^"')]+)["']?\s*\)/.exec(cs.getPropertyValue('border-image-source') || '');
      var sl = (cs.getPropertyValue('border-image-slice') || '').split(/\s+/).map(parseFloat).filter(function (n) { return n > 0; });
      if (!src || !sl.length || !w || !h || !(bw[0] || bw[1] || bw[2] || bw[3])) return null;
      while (sl.length < 4) sl.push(sl[sl.length === 3 ? 1 : 0]);
      // Native texture resolution (slice px per border px), capped for memory.
      var k = 1;
      for (var i = 0; i < 4; i++) if (bw[i] > 0) k = Math.max(k, sl[i] / bw[i]);
      k = Math.min(k, 4096 / Math.max(w, h));
      var c = document.createElement('canvas');
      c.width = Math.round(w * k);
      c.height = Math.round(h * k);
      c.setAttribute('aria-hidden', 'true');
      c.style.cssText = 'position:absolute;pointer-events:none;z-index:20;width:' + w + 'px;height:' + h + 'px;';
      var img = new Image();
      var draw = function () {
        var iw = img.naturalWidth, ih = img.naturalHeight;
        if (!iw || !ih) return;
        var W = c.width, H = c.height;
        var T = Math.round(bw[0] * k), R = Math.round(bw[1] * k), B = Math.round(bw[2] * k), L = Math.round(bw[3] * k);
        var sx = [0, sl[3], iw - sl[1], iw], sy = [0, sl[0], ih - sl[2], ih];
        var dx = [0, L, W - R, W], dy = [0, T, H - B, H];
        var ctx = c.getContext('2d');
        for (var r = 0; r < 3; r++) {
          for (var q = 0; q < 3; q++) {
            if (r === 1 && q === 1) continue;
            var sw = sx[q + 1] - sx[q], sh = sy[r + 1] - sy[r];
            var dw = dx[q + 1] - dx[q], dh = dy[r + 1] - dy[r];
            if (sw > 0 && sh > 0 && dw > 0 && dh > 0) ctx.drawImage(img, sx[q], sy[r], sw, sh, dx[q], dy[r], dw, dh);
          }
        }
        if (onPainted) onPainted();
      };
      img.onload = draw;
      img.src = src[1];
      if (img.complete && img.naturalWidth) { img.onload = null; draw(); }
      return c;
    }

    // The clone is the print with its frame ring: the .custom-frame box plus
    // the 22px ring when a wood frame is on (it paints outside the box).
    function buildClone() {
      var srcFrame = document.querySelector('#print .custom-frame');
      if (!srcFrame) return false;
      // offsetWidth ignores the sticky preview's scale and Room view's
      // matrix3d, which is what we want: the print's real laid-out size.
      var fwPx = srcFrame.offsetWidth, fhPx = srcFrame.offsetHeight;
      if (!fwPx || !fhPx) return false;
      var ring = /\bframe-(black|white|natural|dark-brown)\b/.test(srcFrame.className) ? 22 : 0;
      baseW = fwPx + ring * 2;
      baseH = fhPx + ring * 2;

      var clone = srcFrame.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      // The inner IDs stay: moon-skylit.css places the moon by them
      // (#moon-canvas-container sits ~6% down the print, #moon-canvas is
      // clipped to a circle). Stripping them dropped the moon to the top edge,
      // which cut the unlit limb flat. The clone is appended after the page
      // print and emptied on close, so getElementById still finds the live one.
      clone.removeAttribute('id');
      clone.style.setProperty('width', fwPx + 'px', 'important');
      clone.style.setProperty('height', fhPx + 'px', 'important');
      clone.style.setProperty('left', ring + 'px', 'important');
      clone.style.setProperty('top', ring + 'px', 'important');
      // Canvases clone blank. Copy the pixels across (the moon renderer keeps
      // its drawing buffer for export, so the WebGL canvas reads back).
      var srcCanvases = srcFrame.querySelectorAll('canvas');
      var dstCanvases = clone.querySelectorAll('canvas');
      for (var i = 0; i < srcCanvases.length && i < dstCanvases.length; i++) {
        var sc = srcCanvases[i], dc = dstCanvases[i];
        if (!sc.width || !sc.height) continue;
        dc.width = sc.width; dc.height = sc.height;
        try { dc.getContext('2d').drawImage(sc, 0, 0); } catch (e) { /* noop */ }
      }
      canvas.innerHTML = '';
      canvas.style.width = baseW + 'px';
      canvas.style.height = baseH + 'px';
      canvas.appendChild(clone);
      if (ring) {
        var ringCanvas = paintFrameRing(getComputedStyle(srcFrame, '::after'), baseW, baseH, [ring, ring, ring, ring], function () {
          clone.classList.add('moon-viewer-ring-painted');
        });
        if (ringCanvas) {
          ringCanvas.style.left = '0';
          ringCanvas.style.top = '0';
          canvas.appendChild(ringCanvas);
        }
      }
      return true;
    }

    function measureFit() {
      var r = stage.getBoundingClientRect();
      var pad = r.width < 600 ? 24 : 64;
      fit = Math.min((r.width - pad) / baseW, (r.height - pad) / baseH);
      if (!(fit > 0)) fit = 1;
    }

    function clampPan() {
      var r = stage.getBoundingClientRect();
      var maxX = Math.max(0, (baseW * fit * zoom - r.width) / 2);
      var maxY = Math.max(0, (baseH * fit * zoom - r.height) / 2);
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
    }

    var zoomOutBtn = viewer.querySelector('[data-moon-viewer-zoom="out"]');
    var zoomInBtn = viewer.querySelector('[data-moon-viewer-zoom="in"]');
    var paintedZoom = null;
    var dismissY = 0;        // swipe-down-to-close offset (only at 100%)
    function setTransform() {
      canvas.style.transform = 'translate(-50%, -50%) translate(' + tx + 'px, ' + (ty + dismissY) + 'px) scale(' + (fit * zoom) + ')';
    }
    function apply(animate) {
      clampPan();
      viewer.classList.toggle('is-animating', !!animate);
      setTransform();
      var pct = Math.round(zoom * 100);
      if (pct !== paintedZoom) {
        paintedZoom = pct;
        viewer.classList.toggle('is-zoomed', zoom > 1.001);
        if (level) level.textContent = pct + '%';
        if (zoomOutBtn) zoomOutBtn.disabled = zoom <= MIN + 0.001;
        if (zoomInBtn) zoomInBtn.disabled = zoom >= MAX - 0.001;
      }
    }

    // Zoom to `next`, keeping the stage point (px, py), measured from the
    // stage centre, fixed under the finger/cursor.
    function zoomTo(next, px, py, animate) {
      next = Math.max(MIN, Math.min(MAX, next));
      var k = next / zoom;
      tx = px - (px - tx) * k;
      ty = py - (py - ty) * k;
      zoom = next;
      apply(animate);
    }

    function stagePoint(clientX, clientY) {
      var r = stage.getBoundingClientRect();
      return { x: clientX - r.left - r.width / 2, y: clientY - r.top - r.height / 2 };
    }

    function step(dir) {
      var next = zoom;
      if (dir > 0) {
        for (var i = 0; i < STEPS.length; i++) if (STEPS[i] > zoom + 0.01) { next = STEPS[i]; break; }
      } else {
        for (var j = STEPS.length - 1; j >= 0; j--) if (STEPS[j] < zoom - 0.01) { next = STEPS[j]; break; }
      }
      zoomTo(next, 0, 0, true);
    }

    function open(e) {
      if (!buildClone()) return;
      returnFocus = e && e.currentTarget ? e.currentTarget : document.activeElement;
      viewer.hidden = false;
      prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      zoom = 1; tx = 0; ty = 0;
      measureFit();
      apply(false);
      requestAnimationFrame(function () { viewer.classList.add('is-open'); });
      var closeBtn = viewer.querySelector('[data-moon-viewer-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      document.addEventListener('keydown', onKey);
      window.addEventListener('resize', onResize);
    }

    function close() {
      if (viewer.hidden) return;
      if (fsElement() === viewer) {
        try { (document.exitFullscreen || document.webkitExitFullscreen).call(document); } catch (e) { /* noop */ }
      }
      viewer.classList.remove('is-open', 'is-zoomed', 'is-dragging', 'is-animating', 'is-fullscreen');
      viewer.hidden = true;
      canvas.innerHTML = '';
      document.documentElement.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      pointers = {};
      if (returnFocus && returnFocus.focus && returnFocus.offsetParent !== null) {
        returnFocus.focus({ preventScroll: true });
      }
    }

    function onResize() {
      if (viewer.hidden) return;
      measureFit();
      apply(false);
    }

    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault(); step(1);
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault(); step(-1);
      } else if (e.key === '0') {
        e.preventDefault(); zoom = 1; tx = 0; ty = 0; apply(true);
      } else if (e.key === 'Tab') {
        // Keep focus inside the dialog.
        var f = viewer.querySelectorAll('button:not([disabled]):not([hidden])');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    Array.prototype.forEach.call(openers, function (btn) {
      btn.addEventListener('click', open);
    });
    viewer.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-moon-viewer-close],[data-moon-viewer-zoom]') : null;
      if (!t) return;
      if (t.hasAttribute('data-moon-viewer-close')) { close(); return; }
      var z = t.getAttribute('data-moon-viewer-zoom');
      if (z === 'in') step(1);
      else if (z === 'out') step(-1);
      else if (z === 'reset') { zoom = 1; tx = 0; ty = 0; apply(true); }
    });

    if (fsBtn && fsSupported) {
      fsBtn.addEventListener('click', function () {
        try {
          if (fsElement()) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
          else (viewer.requestFullscreen || viewer.webkitRequestFullscreen).call(viewer);
        } catch (e) { /* noop */ }
      });
      var onFs = function () {
        var isFs = fsElement() === viewer;
        viewer.classList.toggle('is-fullscreen', isFs);
        fsBtn.setAttribute('aria-label', isFs ? 'Exit full screen' : 'Enter full screen');
        requestAnimationFrame(onResize);
      };
      document.addEventListener('fullscreenchange', onFs);
      document.addEventListener('webkitfullscreenchange', onFs);
    }

    // ── Gestures: drag to pan, pinch to zoom, double-tap to toggle ─────────
    var pointers = {};
    var dismissStart = null; // { x, y, t } of the finger that may be dismissing
    var pinchStart = null;   // { dist, zoom }
    var dragLast = null;     // { x, y }
    var lastTap = 0;
    var clickStart = null;   // mouse press { x, y }: a click without a drag toggles zoom

    function pointerList() {
      var out = [];
      for (var k in pointers) if (Object.prototype.hasOwnProperty.call(pointers, k)) out.push(pointers[k]);
      return out;
    }

    // The fade runs on a dedicated backdrop layer and on the bar/hint's own
    // opacity, so the drag only costs a transform and an opacity per frame.
    var backdrop = viewer.querySelector('.moon-viewer__backdrop');
    var barEl = viewer.querySelector('.moon-viewer__bar');
    var dismissRaf = 0;
    function setDim(p) {
      if (backdrop) backdrop.style.opacity = String(1 - p * 0.85);
      var o = String(Math.max(0, 1 - p * 1.4));
      if (barEl) barEl.style.opacity = o;
      if (hint) hint.style.opacity = o;
    }

    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var list = pointerList();
      viewer.classList.remove('is-animating');
      if (list.length === 2) {
        if (dismissY) { dismissY = 0; setDim(0); apply(true); }
        dismissStart = null;
        pinchStart = {
          dist: Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y) || 1,
          zoom: zoom
        };
        dragLast = null;
      } else if (list.length === 1) {
        dragLast = { x: e.clientX, y: e.clientY };
        dismissStart = zoom <= 1.001 ? { x: e.clientX, y: e.clientY, t: Date.now(), active: false } : null;
        var now = Date.now();
        // A mouse click toggles zoom on release (the stage shows a zoom-in
        // cursor, and a double-click was the only thing that zoomed on
        // desktop); double-tap stays the gesture for touch.
        if (e.pointerType === 'mouse') {
          clickStart = { x: e.clientX, y: e.clientY };
        } else if (now - lastTap < 300) {
          var p = stagePoint(e.clientX, e.clientY);
          if (zoom > 1.001) { zoom = 1; tx = 0; ty = 0; apply(true); }
          else zoomTo(2.5, p.x, p.y, true);
          lastTap = 0;
          dragLast = null;
        } else {
          lastTap = now;
        }
      }
    });

    stage.addEventListener('pointermove', function (e) {
      if (!pointers[e.pointerId]) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var list = pointerList();
      if (list.length >= 2 && pinchStart) {
        var d = Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y) || 1;
        var mid = stagePoint((list[0].x + list[1].x) / 2, (list[0].y + list[1].y) / 2);
        zoomTo(pinchStart.zoom * d / pinchStart.dist, mid.x, mid.y, false);
      } else if (list.length === 1 && dragLast) {
        var dx = e.clientX - dragLast.x, dy = e.clientY - dragLast.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) lastTap = 0;
        dragLast = { x: e.clientX, y: e.clientY };
        if (zoom > 1.001) {
          viewer.classList.add('is-dragging');
          tx += dx; ty += dy;
          apply(false);
        } else if (dismissStart) {
          // At 100% a mostly-downward drag pulls the print away: it follows
          // the finger and the backdrop fades, like Photos on iPhone.
          var ddx = e.clientX - dismissStart.x, ddy = e.clientY - dismissStart.y;
          if (!dismissStart.active && ddy > 8 && ddy > Math.abs(ddx) * 1.2) dismissStart.active = true;
          if (dismissStart.active) {
            dismissY = Math.max(0, ddy);
            if (!dismissRaf) {
              canvas.style.willChange = 'transform';
              viewer.classList.remove('is-animating');
              dismissRaf = requestAnimationFrame(function () {
                dismissRaf = 0;
                setTransform();
                setDim(Math.min(1, dismissY / 300));
              });
            }
          }
        }
      }
    });

    function endPointer(e) {
      if (!pointers[e.pointerId]) return;
      if (clickStart && e.type === 'pointerup' && !(dismissStart && dismissStart.active) &&
          Math.abs(e.clientX - clickStart.x) + Math.abs(e.clientY - clickStart.y) < 5) {
        var cp = stagePoint(e.clientX, e.clientY);
        if (zoom > 1.001) { zoom = 1; tx = 0; ty = 0; apply(true); }
        else zoomTo(2.5, cp.x, cp.y, true);
      }
      clickStart = null;
      if (dismissStart && dismissStart.active) {
        if (dismissRaf) { cancelAnimationFrame(dismissRaf); dismissRaf = 0; }
        setTimeout(function () { canvas.style.willChange = ''; }, 260);
        var dt = Math.max(1, Date.now() - dismissStart.t);
        var velocity = dismissY / dt; // px per ms
        dismissStart = null;
        if (dismissY > 110 || (dismissY > 40 && velocity > 0.6)) {
          // Carry on off the bottom, then close.
          dismissY = window.innerHeight;
          setDim(1);
          apply(true);
          setTimeout(function () { close(); dismissY = 0; setDim(0); }, 200);
        } else {
          dismissY = 0;
          setDim(0);
          apply(true);
        }
      }
      delete pointers[e.pointerId];
      var list = pointerList();
      if (list.length < 2) pinchStart = null;
      // Lifting one finger of a pinch hands the pan to the one that stays.
      dragLast = list.length === 1 ? { x: list[0].x, y: list[0].y } : null;
      if (!list.length) viewer.classList.remove('is-dragging');
    }
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);

    stage.addEventListener('wheel', function (e) {
      e.preventDefault();
      var p = stagePoint(e.clientX, e.clientY);
      // Trackpad pinch arrives as ctrl+wheel with small deltas; a mouse wheel
      // notch is ~100. Both map to a smooth exponential zoom.
      var factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0012));
      zoomTo(zoom * factor, p.x, p.y, false);
    }, { passive: false });

    // Safari's proprietary pinch events would otherwise zoom the whole page.
    ['gesturestart', 'gesturechange'].forEach(function (t) {
      viewer.addEventListener(t, function (e) { e.preventDefault(); });
    });
  }

  // ── Phone chrome on the preview: menu + cart ────────────────────────────
  // ≤991px the header bar is collapsed (sections/moon-skylit.liquid). The
  // menu button opens the theme's own drawer; the cart button clicks the
  // hidden header icon (so the drawer behaves as ever) and mirrors the
  // header's count bubble, which the theme keeps current.
  function bindMobileChrome() {
    var btn = document.getElementById('moon-cart-btn');
    var badge = document.getElementById('moon-cart-btn-count');
    var icon = document.getElementById('cart-icon-bubble');
    var menuBtn = document.getElementById('moon-home-btn');
    var drawerToggle = document.querySelector('header-drawer summary');
    if (menuBtn && drawerToggle) {
      menuBtn.addEventListener('click', function (e) {
        e.preventDefault();
        drawerToggle.click();
      });
    }

    if (!btn || !icon) return;
    btn.addEventListener('click', function () { icon.click(); });

    function syncCount() {
      if (!badge) return;
      var bubble = icon.querySelector('.cart-count-bubble');
      // The bubble holds the count twice: the visible digit (span[aria-hidden])
      // and screen-reader text ("5 items"). Take the visible number.
      var n = 0;
      if (bubble) {
        var vis = bubble.querySelector('span[aria-hidden="true"]');
        var m = ((vis ? vis.textContent : bubble.textContent) || '').match(/\d+/);
        n = m ? parseInt(m[0], 10) : 0;
      }
      badge.hidden = n === 0;
      badge.textContent = n > 99 ? '99+' : String(n);
      btn.setAttribute('aria-label', n ? 'Open cart, ' + n + (n === 1 ? ' item' : ' items') : 'Open cart');
    }
    syncCount();
    if (window.MutationObserver) {
      new MutationObserver(syncCount).observe(icon, { childList: true, subtree: true, characterData: true });
    }
  }

  // The toolbar uses the same preference and controller as the storefront.
  function bindThemeToggle() {
    var btn = document.getElementById('moonToolbarTheme');
    if (btn && window.SkylitTheme) window.SkylitTheme.bind(btn);
  }

  // ── Add-to-cart star trail ──────────────────────────────────────────────
  // A comet flies from the button into the basket icon while the cart
  // requests run (assets/skylit-star-trail.js); moon-myjs.js's submit_form
  // calls these. The redirect waits for it to land, a failed add cancels it.
  // Edit mode replaces the design's existing cart line rather than adding
  // one, so the comet still flies but the count stays put.
  var cartFlight = null;
  function launchCartFlight(fromEl, editMode) {
    cartFlight = null;
    if (!window.SkylitStarTrail || !fromEl) return;
    cartFlight = window.SkylitStarTrail.launch(fromEl, [
      document.getElementById('moon-cart-btn'),       // phones: bag on the preview
      document.getElementById('cart-icon-bubble')     // desktop: header bag
    ], {
      onLand: function () {
        if (editMode) return;
        var badge = document.getElementById('moon-cart-btn-count');
        if (!badge) return;
        var n = (parseInt(badge.textContent, 10) || 0) + 1;
        badge.textContent = n > 99 ? '99+' : String(n);
        badge.hidden = false;
      }
    });
  }
  function landCartFlight() {
    var f = cartFlight;
    cartFlight = null;
    return f ? f.land().catch(function () {}) : Promise.resolve();
  }
  function cancelCartFlight() {
    if (cartFlight) cartFlight.cancel();
    cartFlight = null;
  }

  window.MoonStage = {
    launchCartFlight: launchCartFlight,
    landCartFlight: landCartFlight,
    cancelCartFlight: cancelCartFlight
  };

  function init() {
    var main = document.querySelector('.app__main.moon-stage');
    if (!main) return;
    bindMobilePreview(main);
    bindToolsExtras(main);
    bindPrintViewer();
    bindRoomView(main);
    bindMobileChrome();
    bindThemeToggle();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
